import { Button, Center, Container, Flex, Group, Loader, Stack, Stepper } from "@mantine/core";
import { isNotEmpty } from "@mantine/form";
import { equals } from "ramda";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CALENDAR_TYPES } from "../../../constants/calendarConstants";
import { COLORS } from "../../../constants/colorConstants";
import { useCalendarContext } from "../../../contexts/CalendarContext";
import { useLocalAuthorityContext } from "../../../contexts/LocalAuthorityContext";
import useAuth from "../../../hooks/core/useAuth";
import ErrorCard from "../../../shared/components/blocks/ErrorCard";
import { validateEndDate, validateLocalAuthorityId, validateStartDate } from "../utils";
import CreateFormDateSettingsCard from "./components/CreateFormDateSettingsCard";
import CreateFormHeader from "./components/CreateFormHeader";
import CreateFormSelectionCard from "./components/CreateFormSelectionCard";
import CreateFormSettingsCard from "./components/CreateFormSettingsCard";
import CreateFormSubmitCard from "./components/CreateFormSubmitCard";
import { CreateFormProvider, useCreateForm } from "./createFormContext";
import { buildCalendarData } from "./utils/buildCalendarData";
import { buildDateSettings } from "./utils/buildDateSettings";

export default function CreateCalendar() {
  const { createCalendar, upsertCalendarDates } = useCalendarContext();
  const { dropdownFetch: localAuthorityFetch } = useLocalAuthorityContext();
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    localAuthorityFetch.enableDropdownFetch();
  }, [localAuthorityFetch.enableDropdownFetch]);

  const form = useCreateForm({
    mode: "uncontrolled",
    initialValues: {
      name: "",
      startDate: null,
      endDate: null,
      localAuthorityId: "",
      type: "",
      selectedDates: [],
      dateSettings: [],
    },
    validate: {
      name: isNotEmpty("Enter the calendar name"),
      type: isNotEmpty("Please select a type"),
      localAuthorityId: validateLocalAuthorityId,
      startDate: validateStartDate,
      endDate: validateEndDate,
      selectedDates: isNotEmpty("Must select at least 1 day"),
    },
    onValuesChange: (values, previous) => {
      if (values.startDate !== previous.startDate) {
        form.resetField("selectedDates");
        form.resetField("dateSettings");
      }

      if (values.endDate !== previous.endDate) {
        form.resetField("selectedDates");
        form.resetField("dateSettings");
      }

      if (values.type !== previous.type) {
        form.resetField("dateSettings");

        if (values.type === CALENDAR_TYPES.HOLIDAY.id) {
          form.resetField("localAuthorityId");
        }
      }

      // Ensure CreateFormDateSettingsCard gets live values
      if (values.dateSettings.length && !equals(values.dateSettings, previous.dateSettings)) {
        const newDateSettings = values.dateSettings.map((ds) => ({ ...ds }));
        const liveDateSettings = form.values.dateSettings;

        if (!equals(liveDateSettings, newDateSettings)) {
          form.setFieldValue("dateSettings", newDateSettings);
        }
      }
    },
  });

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await new Promise((resolve) => setTimeout(resolve, 0)); // for submit btn label

      const values = form.getValues();

      if (activeStep === 0) {
        const newDateSettings = buildDateSettings(values.selectedDates, values.dateSettings, values.type);

        form.setFieldValue("dateSettings", newDateSettings);
        setActiveStep(1);
      }

      if (activeStep === 1) {
        setActiveStep(2);
        const values = form.getValues();
        const { dateSettings, ...rest } = values;
        const data = buildCalendarData(rest, user);

        const result = await createCalendar.mutateAsync(data);

        if (result?.success) {
          await upsertCalendarDates.mutateAsync({ calendarId: result.id, data: dateSettings });
        }
      }
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderContent = () => {
    if (localAuthorityFetch?.isLoading) {
      return (
        <Center>
          <Loader color={COLORS.DARK_WINE} size="xl" type="bars" />
        </Center>
      );
    }

    if (localAuthorityFetch?.error) {
      return <ErrorCard error={localAuthorityFetch.error} />;
    }

    return (
      <Stack justify="space-between" h="78vh">
        <Stepper active={activeStep} c={COLORS.DARK_BROWN} color={COLORS.DARK_BROWN}>
          <Stepper.Step label="Settings & Selection">
            <Flex gap="xl">
              <CreateFormSettingsCard />
              <CreateFormSelectionCard />
            </Flex>
          </Stepper.Step>
          <Stepper.Step label="Individual Date Settings">
            <CreateFormDateSettingsCard />
          </Stepper.Step>
          <Stepper.Step label="Submit">
            <CreateFormSubmitCard />
          </Stepper.Step>
        </Stepper>
        <Group justify="flex-end" mt="md" p="md">
          <CancelButton activeStep={activeStep} isSubmitting={isSubmitting} />
          <BackStepButton activeStep={activeStep} setActiveStep={setActiveStep} isSubmitting={isSubmitting} />
          <SubmitButton activeStep={activeStep} isSubmitting={isSubmitting} />
        </Group>
      </Stack>
    );
  };

  return (
    <CreateFormProvider form={form}>
      <Container fluid>
        <CreateFormHeader />
        <form onSubmit={form.onSubmit(handleSubmit)}>{renderContent()}</form>
      </Container>
    </CreateFormProvider>
  );
}
function CancelButton({ activeStep, isSubmitting }) {
  const navigate = useNavigate();
  const label = activeStep < 2 ? "Cancel" : "Close";

  return (
    <Button
      variant="outline"
      radius="md"
      c={COLORS.DARK_BROWN}
      disabled={isSubmitting}
      onClick={() => navigate("/calendars")}
    >
      {label}
    </Button>
  );
}

function BackStepButton({ activeStep, setActiveStep, isSubmitting }) {
  return (
    <>
      {activeStep === 1 && (
        <Button
          radius="md"
          c={COLORS.DARK_BROWN}
          bg={COLORS.WHEAT}
          disabled={isSubmitting}
          onClick={() => setActiveStep(0)}
        >
          Back
        </Button>
      )}
    </>
  );
}

function SubmitButton({ activeStep, isSubmitting }) {
  const label = isSubmitting ? <Loader size="sm" color={COLORS.WHEAT} /> : activeStep === 0 ? "Next Step" : "Submit";

  return (
    <>
      {activeStep < 2 && (
        <Button type="submit" radius="md" c={COLORS.WHEAT} bg={COLORS.DARK_BROWN} disabled={isSubmitting}>
          {label}
        </Button>
      )}
    </>
  );
}
